import type { WorkspaceFeatures } from "@expert/features";
import { useFeatures } from "@expert/features";
import type { Logger } from "@expert/logging";
import {
    type CloseGuideSource,
    type EndedSessionProps,
    type OpenGuideSource,
    useGuideStore,
} from "@expert/solve-tools";
import { useEffect } from "react";
import { useVerifiedCustomerStore } from "@/customer-info";
import { useIVRInfo } from "@/customer-info/api";
import { type usePartner, useSessionId, type VoiceTask } from "@/sdk";
import { sdkEventBus } from "@/sdk/agent/eventBus";
import { shouldOpenActivationGuide } from "../device-activation/shouldOpenActivationGuide";
import { shouldOpenInstallationGuide } from "../installation/shouldOpenInstallationGuide";
import { shouldOpenOnCallStart } from "../shouldOpenOnCallStart";

interface UseAutomaticGuideProps {
    activeVoiceTask: VoiceTask | null;
    isCustomerSession: boolean;
    isCustomerVerificationComplete: boolean;
    isGuideOpen: boolean;
    logger: Logger;
    partner: ReturnType<typeof usePartner>;
    closeGuide: (source: CloseGuideSource, endedSession?: EndedSessionProps) => void;
    openGuide: (source: OpenGuideSource, guide?: string) => void;
}

export const useAutomaticGuide = ({
    activeVoiceTask,
    isCustomerSession,
    isCustomerVerificationComplete,
    isGuideOpen,
    logger,
    partner,
    closeGuide,
    openGuide,
}: Readonly<UseAutomaticGuideProps>) => {
    const { disableAutomaticOpen, isLoadingSimplrSession } = useGuideStore();
    const sessionId = useSessionId();
    const { customer } = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId));
    const { intent } = useIVRInfo();
    const {
        features: { isDeviceActivationGuideIntentAutoOpenEnabled, isSimplrGuideEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const { supportNumberCalled, routingQueue: queueName } = activeVoiceTask || {};

    useEffect(() => {
        const sessionChangedEvent = sdkEventBus.on("session_changed", ({ session: endedSession }) => {
            if (isGuideOpen) {
                // TODO: endedSession declared as non nullable. But used with ?. Adding fallback now but we need to check it
                const latestTask = Array.from(endedSession?.tasks.values() ?? []).at(-1) as VoiceTask;
                const callSid = latestTask?.agentCallId;
                closeGuide("auto", { endedSessionId: endedSession.id, endedSessionCallSid: callSid });
            }
        });
        return () => {
            sessionChangedEvent();
        };
    }, [closeGuide, isGuideOpen]);

    // Values reused across all auto open use cases
    const shouldOpenDeps = {
        disableAutomaticOpen,
        isCustomerSession,
        isLoadingSimplrSession,
        isGuideEnabled: isSimplrGuideEnabled,
    };
    const shouldOpenArgs = {
        ...shouldOpenDeps,
        isGuideOpen,
    };

    // Device Activation Guide auto-open
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        const shouldOpenGuide = shouldOpenActivationGuide({
            ...shouldOpenArgs,
            isDeviceActivationGuideIntentAutoOpenEnabled,
            partner,
            supportNumberCalled,
            queueName,
            intent,
        });
        if (shouldOpenGuide) {
            logger.info(
                {
                    ...shouldOpenArgs,
                    isDeviceActivationGuideIntentAutoOpenEnabled,
                    partner,
                    supportNumberCalled,
                    queueName,
                    intent,
                },
                "Solve Guide | Auto opening device activation guide",
            );
            openGuide("auto", "Device Activation");
        }
    }, [intent, isDeviceActivationGuideIntentAutoOpenEnabled, partner, shouldOpenDeps, supportNumberCalled, queueName]);

    // Installation Guide auto-open
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        const shouldOpenGuide = shouldOpenInstallationGuide({
            ...shouldOpenArgs,
            intent: customer?.intent,
            isCustomerVerificationComplete,
        });
        if (shouldOpenGuide) {
            logger.info(
                { ...shouldOpenArgs, intent: customer?.intent, isCustomerVerificationComplete },
                "Solve Guide | Auto opening installation guide",
            );
            openGuide("auto", "Installation");
        }
    }, [customer?.intent, isCustomerVerificationComplete, shouldOpenDeps, partner]);

    // ADT Guide auto-open
    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        const shouldOpenGuide = shouldOpenOnCallStart({
            ...shouldOpenArgs,
        });
        if (shouldOpenGuide) {
            logger.info({ ...shouldOpenArgs }, "Solve Guide | Auto opening ADT guide");
            openGuide("auto", "ADT Guide");
        }
    }, [shouldOpenDeps]);
};
